import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import {
  GroupedStockExportGroupBy,
  StockExportSection,
} from "types";

export type StockExportColumn<T> = {
  id: string;
  header: string;
  value: (row: T) => string | number | null | undefined;
  pdfCellWidth?: number;
  pdfMinCellWidth?: number;
};

type GroupedStockExcelOptions<T> = {
  sections: StockExportSection<T>[];
  columns: StockExportColumn<T>[];
  groupBy: GroupedStockExportGroupBy;
  sheetName: string;
  fileNamePrefix: string;
  headerRows?: string[][];
};

type GroupedStockPdfOptions<T> = {
  sections: StockExportSection<T>[];
  columns: StockExportColumn<T>[];
  groupBy: GroupedStockExportGroupBy;
  title: string;
  subtitle?: string;
  fileNamePrefix: string;
  tableFontSize?: number;
  tableCellPadding?: number;
};

type FlatStockExcelOptions<T> = {
  rows: T[];
  columns: StockExportColumn<T>[];
  sheetName: string;
  fileNamePrefix: string;
  headerRows?: string[][];
};

type FlatStockPdfOptions<T> = {
  rows: T[];
  columns: StockExportColumn<T>[];
  title: string;
  subtitle?: string;
  fileNamePrefix: string;
  tableFontSize?: number;
  tableCellPadding?: number;
};

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: {
    finalY?: number;
  };
};

type PdfHeaderOptions = {
  title: string;
  subtitle?: string;
};

const groupColumnByExportType: Record<GroupedStockExportGroupBy, string> = {
  location: "location",
  article: "article",
};

const getVisibleColumns = <T,>(
  columns: StockExportColumn<T>[],
  groupBy: GroupedStockExportGroupBy,
) => columns.filter((column) => column.id !== groupColumnByExportType[groupBy]);

const formatCellValue = (value: string | number | null | undefined) =>
  value == null || value === "" ? "-" : value;

const buildSubtotalRow = <T,>(
  columns: StockExportColumn<T>[],
  section: StockExportSection<T>,
) => {
  const subtotalRow = Array(columns.length).fill("");
  const quantityColumnIndex = columns.findIndex((column) => column.id === "quantity");
  const saldoColumnIndex = columns.findIndex((column) => column.id === "saldo");

  subtotalRow[0] = "SUBTOTALES";

  if (quantityColumnIndex >= 0) {
    subtotalRow[quantityColumnIndex] = section.totals.quantity;
  }

  if (saldoColumnIndex >= 0) {
    subtotalRow[saldoColumnIndex] = section.totals.saldo;
  }

  return subtotalRow;
};

const buildSectionHeaderRow = <T,>(
  columns: StockExportColumn<T>[],
  section: StockExportSection<T>,
) => [section.label, ...Array(Math.max(columns.length - 1, 0)).fill("")];

const assertHasSections = <T,>(sections: StockExportSection<T>[]) => {
  if (!sections.length) {
    throw new Error("No hay stock disponible para exportar con los filtros actuales.");
  }
};

const assertHasRows = <T,>(rows: T[]) => {
  if (!rows.length) {
    throw new Error("No hay stock disponible para exportar con los filtros actuales.");
  }
};

const saveWorkbook = (
  rows: (string | number | null | undefined)[][],
  sheetName: string,
  fileNamePrefix: string,
) => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${fileNamePrefix}_${dayjs().format("YYYY-MM-DD")}.xlsx`);
};

const buildPdfColumnStyles = <T,>(columns: StockExportColumn<T>[]) =>
  columns.reduce<Record<number, Record<string, unknown>>>(
    (styles, column, index) => {
      if (
        column.pdfCellWidth === undefined &&
        column.pdfMinCellWidth === undefined
      ) {
        return styles;
      }

      styles[index] = {
        ...(column.pdfCellWidth !== undefined
          ? { cellWidth: column.pdfCellWidth }
          : {}),
        ...(column.pdfMinCellWidth !== undefined
          ? { minCellWidth: column.pdfMinCellWidth }
          : {}),
      };

      return styles;
    },
    {},
  );

const addPdfHeader = (
  doc: jsPDF,
  { title, subtitle }: PdfHeaderOptions,
) => {
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  doc.setFontSize(9);
  doc.text(`Fecha: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 14, 22);
  if (subtitle) doc.text(subtitle, 14, 27);
};

const addPdfPageNumber = (doc: jsPDF) => {
  const pageNumber = doc.getNumberOfPages();
  const pageSize = doc.internal.pageSize;

  doc.setFontSize(8);
  doc.text(
    `Pagina ${pageNumber}`,
    pageSize.getWidth() - 20,
    pageSize.getHeight() - 10,
    { align: "right" },
  );
};

export const exportGroupedStockToExcel = <T,>({
  sections,
  columns,
  groupBy,
  sheetName,
  fileNamePrefix,
  headerRows = [],
}: GroupedStockExcelOptions<T>) => {
  assertHasSections(sections);

  const visibleColumns = getVisibleColumns(columns, groupBy);
  const bodyRows: (string | number | null | undefined)[][] = [];

  sections.forEach((section) => {
    bodyRows.push([section.label]);
    bodyRows.push(visibleColumns.map((column) => column.header));

    section.rows.forEach((row) => {
      bodyRows.push(
        visibleColumns.map((column) => formatCellValue(column.value(row))),
      );
    });

    bodyRows.push(buildSubtotalRow(visibleColumns, section));
    bodyRows.push([]);
  });

  saveWorkbook([...headerRows, ...bodyRows], sheetName, fileNamePrefix);
};

export const exportStockToExcel = <T,>({
  rows,
  columns,
  sheetName,
  fileNamePrefix,
  headerRows = [],
}: FlatStockExcelOptions<T>) => {
  assertHasRows(rows);

  const bodyRows = [
    columns.map((column) => column.header),
    ...rows.map((row) =>
      columns.map((column) => formatCellValue(column.value(row))),
    ),
  ];
  saveWorkbook([...headerRows, ...bodyRows], sheetName, fileNamePrefix);
};

export const exportGroupedStockToPdf = <T,>({
  sections,
  columns,
  groupBy,
  title,
  subtitle,
  fileNamePrefix,
  tableFontSize = 7,
  tableCellPadding = 1,
}: GroupedStockPdfOptions<T>) => {
  assertHasSections(sections);

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const visibleColumns = getVisibleColumns(columns, groupBy);
  const columnStyles = buildPdfColumnStyles(visibleColumns);

  addPdfHeader(doc, { title, subtitle });

  let startY = subtitle ? 35 : 30;

  sections.forEach((section) => {
    const subtotalRow = buildSubtotalRow(visibleColumns, section);

    autoTable(doc, {
      startY,
      head: [
        buildSectionHeaderRow(visibleColumns, section),
        visibleColumns.map((column) => column.header),
      ],
      body: [
        ...section.rows.map((row) =>
          visibleColumns.map((column) => formatCellValue(column.value(row))),
        ),
        subtotalRow,
      ],
      styles: {
        fontSize: tableFontSize,
        cellPadding: tableCellPadding,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: 0,
        fontStyle: "bold",
      },
      columnStyles,
      didParseCell: (dataCell) => {
        if (dataCell.section === "head" && dataCell.row.index === 0) {
          dataCell.cell.colSpan = visibleColumns.length;
        }

        if (
          dataCell.section === "body" &&
          dataCell.row.index === section.rows.length
        ) {
          dataCell.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: () => {
        addPdfPageNumber(doc);
      },
    });

    startY = ((doc as JsPdfWithAutoTable).lastAutoTable?.finalY ?? startY) + 8;
  });

  doc.save(`${fileNamePrefix}_${dayjs().format("YYYY-MM-DD")}.pdf`);
};

export const exportStockToPdf = <T,>({
  rows,
  columns,
  title,
  subtitle,
  fileNamePrefix,
  tableFontSize = 7,
  tableCellPadding = 1,
}: FlatStockPdfOptions<T>) => {
  assertHasRows(rows);

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const columnStyles = buildPdfColumnStyles(columns);

  addPdfHeader(doc, { title, subtitle });

  autoTable(doc, {
    startY: subtitle ? 35 : 30,
    head: [columns.map((column) => column.header)],
    body: rows.map((row) =>
      columns.map((column) => formatCellValue(column.value(row))),
    ),
    styles: {
      fontSize: tableFontSize,
      cellPadding: tableCellPadding,
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: 0,
      fontStyle: "bold",
    },
    columnStyles,
    didDrawPage: () => {
      addPdfPageNumber(doc);
    },
  });

  doc.save(`${fileNamePrefix}_${dayjs().format("YYYY-MM-DD")}.pdf`);
};
